/**
 * Created by jv on 2019-09-02 (15:28).
 */
import utils from "Sources/utils/utils";
import AppError from "Sources/models/app-error";

const log = require("Sources/utils/logger")();

class Sector {
  constructor(options) {
    if (utils.isNull(options)) throw new AppError({exception: new Error("Options for BusinessModel cannot be null")});

    this.businessModel = options.businessModel;

    this.id = this.businessModel.id;

    this.containerElement = this.businessModel.containerElement;
    this.layoutData = this.businessModel.layoutData;

    this.canvas = this.businessModel.canvas;
    this.sectorGroup = null;

    this.sectorData = options.sectorData;
    this.key = this.sectorData.key;

    this.backgroundObj = null;
    this.iconObj = null;
    this.iconActiveObj = null;
    this.textObj = null;

    this.active = false;

    this.previewBackground = null;

    this.iconActiveSource = null;

    this.animationsRunning = 0;
    this.shouldAbortAnimations = false;

    this.hasRendered = false;
    this.onRenderComplete = options.onRenderComplete;
  }

  render() {
    this.hasRendered = false;

    this.businessModel.drawDebugInfo(this.key, this.sectorData.position, "#FF0000", this.layoutData.sectorSettings.size.width / 2);

    this.businessModel.loadSvg(this.sectorData.icon)
      .then((iconInactive) => {
        this.iconActiveSource = this.sectorData.iconActive;

        if (utils.isNotNull(this.sectorData.iconActive)) {
          this.businessModel.loadSvg(this.sectorData.iconActive)
            .then((iconActive) => {
              this._onIconsLoaded(iconInactive, iconActive);
            })
            .catch((err) => {
              log.error("Failed to load sector icon %s - %o", this.sectorData.iconActive, err);
            });
        } else {
          this._onIconsLoaded(iconInactive, null);
        }
      })
      .catch((err) => {
        log.error("Failed to load sector icon %s - %o", this.sectorData.icon, err);
      });
  }

  _onIconsLoaded(iconInactive, iconActive) {
    let groupArray = [];

    this.backgroundObj = this.setSectorBackground();
    groupArray.push(this.backgroundObj);

    // InactiveIcon
    this.iconObj = this.setSectorIcon(iconInactive);
    groupArray.push(this.iconObj);

    if (utils.isNotNull(iconActive)) {
      // ActiveIcon
      this.iconActiveObj = this.setSectorIcon(iconActive);
      this.iconActiveObj.set({opacity: 0});
      groupArray.push(this.iconActiveObj);
    }

    if (!utils.isFunction(this.sectorData.name)) {
      this.sectorData.name = () => {
        return ""
      };
    }

    this.textObj = this.setSectorName();
    groupArray.push(this.textObj);

    groupArray.push(this.createPreviewOverlay());

    this.sectorGroup = new fabric.Group(groupArray, {
      objectCaching: false
    });

    this.hasRendered = true;

    if (utils.isFunction(this.onRenderComplete)) {
      this.onRenderComplete.call(this);
    }
  }

  createPreviewOverlay() {
    this.previewBackground = this.setSectorBackground();
    this.previewBackground.set({
      fill: "transparent",
      stroke: "transparent",
      strokeDashArray: null,
      strokeWidth: 2,
      visible: false
    });

    return this.previewBackground;
  }

  setSectorBackground() {
    let sectorBackground = new fabric.Rect({
      width: this.layoutData.sectorSettings.size.width, height: this.layoutData.sectorSettings.size.height,
      left: this.sectorData.position.x, top: this.sectorData.position.y,
      fill: this.layoutData.sectorSettings.backgroundColor,
      angle: 0,
      rx: this.layoutData.sectorSettings.radius.rx,
      ry: this.layoutData.sectorSettings.radius.ry,
      selectable: false,
      hoverCursor: "default"
    });

    if (this.sectorData.startsEmpty) {
      sectorBackground.set({
        stroke: this.layoutData.sectorSettings.strokeColor,
        strokeDashArray: [5, 5],
        strokeWidth: 2
      });
    }

    sectorBackground.setShadow({
      color: "rgba(0, 0, 0, 0.3)",
      blur: 3,
      offsetX: 0,
      offsetY: 0
    });

    return sectorBackground;
  }

  setSectorIcon(fabricObj) {
    if (utils.isNotNull(fabricObj)) {
      fabricObj.originX = "center";
      fabricObj.originY = "center";

      fabricObj.top = this.backgroundObj.top + (this.layoutData.sectorSettings.size.height / 2);
      fabricObj.left = this.backgroundObj.left + (this.layoutData.sectorSettings.size.width / 2);

      fabricObj.evented = false;
      fabricObj.selectable = false;
      fabricObj.hasBorders = false;
      fabricObj.hasControls = false;
      fabricObj.reference = false;

      return fabricObj;
    }

    return null;
  }

  setSectorName() {
    let sectorName = this.sectorData.name().toUpperCase();

    return new fabric.Textbox(sectorName, {
      width: this.layoutData.sectorSettings.size.width * 0.9,
      height: this.layoutData.sectorSettings.size.height * 0.2,
      originX: "center",
      originY: "bottom",
      top: this.backgroundObj.top + this.layoutData.sectorSettings.size.height - 10,
      left: this.backgroundObj.left + (this.layoutData.sectorSettings.size.width / 2),

      fontFamily: "Rubik",
      fontSize: 12,
      charSpacing: 20,
      textAlign: "center",

      evented: false,
      selectable: false,
      hasBorders: false,
      hasControls: false,
      reference: false
    });
  }

  setActiveIcon(source, type) {
    if (source === this.iconActiveSource) return;
    this.iconActiveSource = source;

    return this.businessModel.loadSvg(source)
      .then((iconActive) => {
        if (utils.isNotNull(this.iconActiveObj)) {
          this.sectorGroup.remove(this.iconActiveObj);
        }

        this.iconActiveObj = this.setSectorIcon(iconActive);
        this.sectorGroup.add(this.iconActiveObj);

        this.canvas.renderAll();
      })
      .catch((err) => {
        log.error("Failed to load sector icon %s - %o", source, err);
      });
  }

  setText(text) {
    this.textObj.set({text: text.toUpperCase()});
    this.canvas.renderAll();
  }

  preview(animationOptions, render) {
    this.previewBackground.set({
      visible: true,
      stroke: animationOptions.sectorStrokeColorPreview || "transparent",
      strokeDashArray: null,
      strokeWidth: animationOptions.sectorStrokeWidthPreview || 0
    });

    if (render) this.canvas.renderAll();

    return this;
  }

  hidePreview(render) {
    this.previewBackground.set({visible: false});

    if (render) this.canvas.renderAll();
  }

  activate(animationOptions) {
    log.info("Activating sector for key [%s] - %o", this.key, animationOptions);

    animationOptions = utils.isNotNull(animationOptions) ? animationOptions : {};

    if (utils.isNull(this.iconActiveObj)) {
      throw new AppError({exception: new Error("Sector doesn't have an active icon - first call sector.setActiveIcon before activating")});
    }

    if (animationOptions.instant) {
      this.backgroundObj.set({
        fill: animationOptions.sectorBackgroundColorPreview || this.layoutData.sectorSettings.backgroundColorActive,
        stroke: "transparent",
        strokeDashArray: null,
        strokeWidth: animationOptions.sectorStrokeWidthPreview || 0
      });

      this.iconObj.set({opacity: 0});
      this.iconActiveObj.set({opacity: 1});
    } else {
      let animationDuration = this.layoutData.animationDuration || 300;

      this.animationsRunning++;
      this.iconObj.animate("opacity", 0, {
        duration: animationDuration,
        onComplete: () => {
          this.animationsRunning--;
          this.setShouldAbortAnimations();
        },
        abort: () => this.shouldAbortAnimations
      });

      this.animationsRunning++;
      this.iconActiveObj.animate("opacity", 1, {
        duration: animationDuration,
        onComplete: () => {
          this.animationsRunning--;
          this.setShouldAbortAnimations();
        },
        abort: () => this.shouldAbortAnimations
      });

      let fromColor = this.layoutData.sectorSettings.backgroundColor,
        toColor = animationOptions.sectorBackgroundColorPreview || this.layoutData.sectorSettings.backgroundColorActive;

      this.animationsRunning++;
      fabric.util.animateColor(fromColor, toColor, animationDuration, {
        onChange: (value) => {
          this.backgroundObj.set("fill", value);
          this.backgroundObj.canvas.renderAll();
        },
        onComplete: () => {
          this.animationsRunning--;

          this.setShouldAbortAnimations();

          this.backgroundObj.set({
            fill: toColor,
            stroke: animationOptions.sectorStrokeColorPreviewActive || "transparent",
            strokeDashArray: null,
            strokeWidth: animationOptions.sectorStrokeWidthPreview || 2
          });

          this.active = false;

          resolve();
        },
        abort: () => {
          return this.shouldAbortAnimations;
        }
      });

      if (this.active && !animationOptions.instant) {
        this.flash(this.layoutData.sectorSettings.backgroundColorActive, this.layoutData.sectorSettings.backgroundColor);
      }
    }

    this.active = true;

    this.canvas.renderAll();
  }

  flash(startColor, endColor) {
    // %TODO: better 'already active' animation

    let pulseCount = 5;
    let pulseDuration = 1000 / pulseCount;
    let i = 0;

    let pulseAnimation = (fromColor, toColor) => {
      if (i++ > pulseCount || this.shouldAbortAnimations) return;

      this.animationsRunning++;
      fabric.util.animateColor(fromColor, toColor, pulseDuration, {
        onChange: (value) => {
          this.backgroundObj.set("fill", value);
          this.backgroundObj.canvas.renderAll();
        },
        onComplete: () => {
          this.animationsRunning--;

          if (this.shouldAbortAnimations) {
            this.setShouldAbortAnimations();
          } else {
            if (i % 2 === 0) {
              pulseAnimation(this.layoutData.sectorSettings.backgroundColorActive, this.layoutData.sectorSettings.backgroundColor);
            } else {
              pulseAnimation(this.layoutData.sectorSettings.backgroundColor, this.layoutData.sectorSettings.backgroundColorActive);
            }
          }
        },
        abort: () => {
          return this.shouldAbortAnimations;
        }
      });
    };

    pulseAnimation(startColor, endColor);
  }

  deactivate(animationOptions) {
    log.info("Deactivating sector for key [%s]", this.key);

    animationOptions = utils.isNotNull(animationOptions) ? animationOptions : {};

    // Copy object
    animationOptions = Object.assign({}, animationOptions, {instant: true});

    return new Promise((resolve, reject) => {
      let animationDuration = (animationOptions.instant) ? 1 : this.layoutData.animationDuration;

      if (animationOptions.instant) {
        this.iconObj.set({opacity: 1});
        this.iconActiveObj.set({opacity: 0});

        this.backgroundObj.set("fill", this.layoutData.sectorSettings.backgroundColor);

        if (animationOptions.isPreview) {
          this.backgroundObj.set({
            stroke: animationOptions.sectorStrokeColorPreviewInactive || this.layoutData.sectorSettings.strokeColor,
            strokeDashArray: null,
            strokeWidth: animationOptions.sectorStrokeWidthPreview || 2
          });
        } else if (this.sectorData.startsEmpty) {
          this.backgroundObj.set({
            stroke: animationOptions.sectorStrokeColorPreviewInactive || this.layoutData.sectorSettings.strokeColor,
            strokeDashArray: [5, 5],
            strokeWidth: 2
          });
        }

        this.active = false;

        resolve();
      } else {
        this.animationsRunning++;
        this.iconObj.animate("opacity", 1, {
          duration: animationDuration,
          onComplete: () => {
            this.animationsRunning--;
            this.setShouldAbortAnimations();
          },
          abort: () => this.shouldAbortAnimations
        });

        this.animationsRunning++;
        this.iconActiveObj.animate("opacity", 0, {
          duration: animationDuration,
          onComplete: () => {
            this.animationsRunning--;
            this.setShouldAbortAnimations();
          },
          abort: () => this.shouldAbortAnimations
        });

        let fromColor = this.layoutData.sectorSettings.backgroundColorActive,
          toColor = animationOptions.sectorBackgroundColorPreview || this.layoutData.sectorSettings.backgroundColor;

        this.animationsRunning++;
        fabric.util.animateColor(fromColor, toColor, animationDuration, {
          onChange: (value) => {
            this.backgroundObj.set("fill", value);
            this.backgroundObj.canvas.renderAll();
          },
          onComplete: () => {
            this.animationsRunning--;

            this.setShouldAbortAnimations();

            if (this.shouldAbortAnimations) {
              this.backgroundObj.set("fill", this.layoutData.sectorSettings.backgroundColor);
            }

            if (animationOptions.isPreview) {
              this.backgroundObj.set({
                stroke: animationOptions.sectorStrokeColorPreviewInactive || this.layoutData.sectorSettings.strokeColor,
                strokeDashArray: null,
                strokeWidth: animationOptions.sectorStrokeWidthPreview || 2
              });
            } else if (this.sectorData.startsEmpty) {
              this.backgroundObj.set({
                stroke: animationOptions.sectorStrokeColorPreviewInactive || this.layoutData.sectorSettings.strokeColor,
                strokeDashArray: [5, 5],
                strokeWidth: 2
              });
            }

            this.active = false;

            resolve();
          },
          abort: () => {
            return this.shouldAbortAnimations;
          }
        });
      }
    });
  }

  setShouldAbortAnimations() {
    if (this.shouldAbortAnimations && this.animationsRunning <= 0) {
      this.shouldAbortAnimations = false;
    }
  }

  abortAnimations() {
    if (this.animationsRunning > 0) {
      this.shouldAbortAnimations = true;
    }
  }
}

export default Sector;



// WEBPACK FOOTER //
// ./app/js/components/business-model/sector.js